import type {
  ColumnPinningPosition,
  ColumnPinningState,
  RowData,
} from '@tanstack/react-table';
import { JSX, useCallback, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { PinIcon, UnpinIcon } from '@dynatrace/strato-icons';

import { Button } from '../../../../../buttons/button/Button.js';
import { attemptFocus } from '../../../../../core/utils/focus-management/attempt-focus.js';
import { Menu } from '../../../../../navigation/menu/Menu.js';
import { Tooltip } from '../../../../../overlays/tooltip/Tooltip.js';
import { DATATABLE_COMPONENT_NAME } from '../../../constants.js';
import {
  pinColumns,
  toPhysicalPinnedPosition,
} from '../../../features/ColumnPinning/column-pinning-utils.js';
import type { TDataTableFeatures } from '../../../features/types/features.types.js';
import { useRestoreFocusOnMenuClose } from '../../../features/UserActions/useRestoreFocusOnMenuClose.js';
import { dataTableMessages } from '../../../messages.js';
import type { ColumnListItem } from './column-settings-types.js';

interface ColumnPinningMenuProps<TData extends RowData> {
  /**
   * The column to render the pinning menu for.
   */
  column: ColumnListItem<TDataTableFeatures, TData>;
  /**
   * The ordered flat columns, grouped by pinning position.
   */
  orderedFlatColumns: {
    left: ColumnListItem<TDataTableFeatures, TData>[];
    center: ColumnListItem<TDataTableFeatures, TData>[];
    right: ColumnListItem<TDataTableFeatures, TData>[];
  };
  /**
   * The column pinning state in the settings modal.
   */
  localColumnPinning: ColumnPinningState;
  /**
   * Callback to update the pinning state in the settings modal.
   */
  updateLocalColumnPinning: (newState: ColumnPinningState) => void;
}

/**
 * Menu component for pinning/unpinning columns in the DataTable column settings.
 * @internal
 */
export function ColumnPinningMenu<TData extends RowData>(
  props: Readonly<ColumnPinningMenuProps<TData>>,
): JSX.Element {
  const {
    column,
    orderedFlatColumns,
    localColumnPinning,
    updateLocalColumnPinning,
  } = props;

  const intl = useIntl();
  const columnId = column.id;
  const pinnedPosition = column.pinned;
  const pinningLabel = intl.formatMessage(dataTableMessages.pinColumnLabel, {
    isUnpinned: pinnedPosition === false,
  });

  const [menuOpen, setMenuOpen] = useState(false);
  const { triggerRef, onCloseAutoFocus } =
    useRestoreFocusOnMenuClose<HTMLButtonElement>(menuOpen);
  const pinActionTakenRef = useRef(false);

  /**
   * Returns all columns that belong to the same pinning region as the current column.
   * If the current column is not pinned, the function returns the unpinned ("center") columns.
   *
   * @returns An array of columns from the same pinning region (left, right, or unpinned).
   */
  function getColumnsInSamePinningRegion(): ColumnListItem<
    TDataTableFeatures,
    TData
  >[] {
    const position = toPhysicalPinnedPosition(pinnedPosition) || 'center';
    return orderedFlatColumns[position];
  }

  /**
   * Returns the ID of the current column or, if it has nested columns,
   * the IDs of its nested child columns in the same pinning region (left, right, or unpinned).
   *
   * @returns An array of column IDs that should be pinned or unpinned together.
   */
  function getIdsToPin(): string[] {
    const hasNestedColumns = column.columns.length;
    if (!hasNestedColumns) {
      return [columnId];
    }

    const idsToPin: string[] = [];
    const columns = getColumnsInSamePinningRegion();

    // find parent column
    // use key instead of ID, as the same parent (with same ID) can appear multiple times,
    // even within the same pinned area
    const startIndex = columns.findIndex((col) => col.key === column.key);
    if (startIndex < 0) {
      return [];
    }
    // get first nested child
    let index = startIndex + 1;
    let current = columns.at(index);
    // collect all adjacent children
    while (current?.parent?.id === columnId) {
      idsToPin.push(current.id);
      index++;
      current = columns.at(index);
    }
    return idsToPin;
  }

  const pin = (position: ColumnPinningPosition): void => {
    pinActionTakenRef.current = true;
    const idsToPin = getIdsToPin();
    const newState = pinColumns(idsToPin, localColumnPinning, position);
    updateLocalColumnPinning(newState);
  };

  // When a pin action moves the column, the list item remounts with a new key and triggerRef
  // is null. Fall back to finding the pin button at the column's new position via DOM query.
  const handleCloseAutoFocus = useCallback(
    (event: Event): void => {
      onCloseAutoFocus(event);
      if (!event.defaultPrevented && pinActionTakenRef.current) {
        pinActionTakenRef.current = false;
        const button = document.querySelector<HTMLElement>(
          `[data-columnid="${CSS.escape(columnId)}"] [data-dt-children-name="OpenColumnSettingsPinningActions"]`,
        );
        if (button) {
          attemptFocus(button, { focusVisible: true });
          event.preventDefault();
        }
      }
    },
    [columnId, onCloseAutoFocus],
  );

  const pinningMenuIcon = (
    <Button.Prefix>
      {pinnedPosition === false ? <PinIcon /> : <UnpinIcon />}
    </Button.Prefix>
  );

  return (
    <Tooltip text={pinningLabel} placement="right" disabled={menuOpen}>
      {/* Radix's DropdownMenu trigger calls event.preventDefault() on pointerdown when
      opening, which suppresses the click event that we need for behavior tracking.
      Using a plain Button outside Menu.Trigger ensures the click event fires normally. */}
      {!menuOpen ? (
        <Button
          ref={triggerRef}
          size="condensed"
          aria-label={pinningLabel}
          aria-haspopup="menu"
          aria-expanded={false}
          data-dt-children-name="OpenColumnSettingsPinningActions"
          onClick={() => setMenuOpen(true)}
        >
          {pinningMenuIcon}
        </Button>
      ) : (
        <Menu
          open={menuOpen}
          onOpenChange={setMenuOpen}
          data-dt-component={DATATABLE_COMPONENT_NAME}
        >
          <Menu.Trigger>
            <Button
              size="condensed"
              aria-label={pinningLabel}
              data-dt-children-name="OpenColumnSettingsPinningActions"
            >
              {pinningMenuIcon}
            </Button>
          </Menu.Trigger>
          <Menu.Content onCloseAutoFocus={handleCloseAutoFocus}>
            {pinnedPosition !== 'start' && (
              <Menu.Item
                onSelect={() => pin('start')}
                data-dt-children-name="ColumnSettingsPinLeft"
              >
                <FormattedMessage {...dataTableMessages.pinColumnLeftLabel} />
              </Menu.Item>
            )}
            {pinnedPosition !== 'end' && (
              <Menu.Item
                onSelect={() => pin('end')}
                data-dt-children-name="ColumnSettingsPinRight"
              >
                <FormattedMessage {...dataTableMessages.pinColumnRightLabel} />
              </Menu.Item>
            )}
            {pinnedPosition !== false && (
              <Menu.Item
                onSelect={() => pin(false)}
                data-dt-children-name="ColumnSettingsUnpin"
              >
                <FormattedMessage {...dataTableMessages.unpinColumnLabel} />
              </Menu.Item>
            )}
          </Menu.Content>
        </Menu>
      )}
    </Tooltip>
  );
}
